import React, { useMemo, memo } from 'react';
import { Box, Paper, Typography, Skeleton, Tooltip } from '@mui/material';
import { TERMS } from '../../config/terminology';
import {
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  Send as SendIcon,
  Approval as ApprovalIcon,
  LocalShipping as ShippingIcon,
  TrendingUp as TrendingUpIcon,
  CompareArrows as CompareIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
// IQVIA Brand Color Palette
const IQVIA_COLORS = {
  lightBlue: '#00A3E0',
  darkBlue: '#005587',
  lightGreen: '#43B02A',
  darkGreen: '#027223',
  lightTeal: '#00BFB3',
  darkTeal: '#008F86',
  orange: '#FE8A12',
  charcoal: '#2B3A42',
};

const CHART_COLORS = [
  IQVIA_COLORS.darkBlue,
  IQVIA_COLORS.lightTeal,
  IQVIA_COLORS.lightGreen,
  IQVIA_COLORS.lightBlue,
  IQVIA_COLORS.darkTeal,
  IQVIA_COLORS.darkGreen,
  IQVIA_COLORS.orange,
];

const ICON_MAP = {
  adherence: AssessmentIcon,
  risk: WarningIcon,
  submission: SendIcon,
  approval: ApprovalIcon,
  emergency: ShippingIcon,
  excess: TrendingUpIcon,
  breach: CompareIcon,
  growth: ChartIcon,
  default: AssessmentIcon,
};

const truncateName = (name, maxLength = 16) => {
  if (!name) return '';
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength) + '...';
};

const DEMO_DATA_TOOLTIP = 'Demo data for illustration';

const MiniChartCard = memo(({
  title,
  chartType = 'horizontalBar',
  data = [],
  isLoading = false,
  nameKey = 'name',
  valueKey = 'value',
  valueKeys = [],
  unit = '',
  iconType = 'default',
  showPercentage = false,
  isRatio = false,
  showRatioAsPercent = false,
  stretchToRow = false,
  minHeightOverride = null,
  isDemoData = false,
}) => {
  const effectiveData = useMemo(() => {
    if (isLoading) return [];
    const sourceData = data || [];

    // For ratio-as-percent charts we display absolute values; sort by that
    // displayed magnitude so the visual order matches the labels.
    if (isRatio && showRatioAsPercent) {
      return [...sourceData].sort((a, b) => {
        const aVal = Math.abs(Number(a?.[valueKey]) || 0);
        const bVal = Math.abs(Number(b?.[valueKey]) || 0);
        return bVal - aVal;
      });
    }

    return sourceData;
  }, [data, isLoading, isRatio, showRatioAsPercent, valueKey]);

  // Determine which value keys to use
  const effectiveValueKeys = useMemo(() => {
    if (valueKeys && valueKeys.length > 0) {
      return valueKeys;
    }
    return [valueKey];
  }, [valueKey, valueKeys]);

  // Prepare chart data for bar charts
  const barChartData = useMemo(() => {
    if (!effectiveData || effectiveData.length === 0) return { categories: [], fullNames: [], series: [], total: 0 };

    // Store both truncated labels and full names for tooltips
    const fullNames = effectiveData.map(item => item[nameKey] || '');
    const categories = fullNames.map(name => truncateName(name, 16));

    const series = effectiveValueKeys.map((key) => ({
      name: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim(),
      data: effectiveData.map(item => {
        const val = item[key];
        if (val === undefined || val === null || val === '') return 0;
        const numVal = typeof val === 'number' ? val : parseFloat(val);
        if (isNaN(numVal)) return 0;
        // Use absolute value for ratio percentages to ensure positive bars
        return (isRatio && showRatioAsPercent) ? Math.abs(numVal) : numVal;
      })
    }));

    const total = series[0]?.data?.reduce((a, b) => a + b, 0) || 0;

    return { categories, fullNames, series, total };
  }, [effectiveData, nameKey, effectiveValueKeys, isRatio, showRatioAsPercent]);

  // Prepare chart data for donut chart
  const donutChartData = useMemo(() => {
    if (!effectiveData || effectiveData.length === 0) return { labels: [], fullNames: [], series: [], total: 0 };

    // Store both truncated labels and full names for tooltips
    const fullNames = effectiveData.map(item => item[nameKey] || '');
    const labels = fullNames.map(name => truncateName(name, 16));

    const series = effectiveData.map(item => {
      const val = item[effectiveValueKeys[0]];
      if (val === undefined || val === null || val === '') return 0;
      const numVal = typeof val === 'number' ? val : parseFloat(val);
      return isNaN(numVal) ? 0 : numVal;
    });

    const total = series.reduce((a, b) => a + b, 0);

    return { labels, fullNames, series, total };
  }, [effectiveData, nameKey, effectiveValueKeys]);

  // Format value for display
  const formatValue = (val, includeUnit = true) => {
    if (val === undefined || val === null) return '0';
    const numVal = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(numVal)) return '0';
    
    if (isRatio) {
      if (showRatioAsPercent) {
        // Show ratio as percentage (e.g., 3.31 -> 331%)
        // Use absolute value for display if negative
        const absVal = Math.abs(numVal);
        return (absVal * 100).toFixed(0) + '%';
      }
      // For ratio values, show as ratio (e.g., 2.49x)
      return numVal.toFixed(2) + 'x';
    }
    
    if (numVal >= 1000000) {
      return (numVal / 1000000).toFixed(1) + 'M' + (includeUnit && unit ? ` ${unit}` : '');
    }
    if (numVal >= 1000) {
      return (numVal / 1000).toFixed(0) + 'K' + (includeUnit && unit ? ` ${unit}` : '');
    }
    return numVal.toLocaleString() + (includeUnit && unit ? ` ${unit}` : '');
  };

  // Format a number without abbreviations (e.g., 1250 -> "1,250")
  const formatFullNumber = (val) => {
    if (val === undefined || val === null) return '0';
    const numVal = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(numVal)) return '0';
    if (Number.isInteger(numVal)) return numVal.toLocaleString();
    // For non-integers, show up to 2 decimals but strip trailing .00
    const s = numVal.toFixed(2);
    return s.replace(/\.00$/, '').replace(/\.?0+$/, '');
  };

  // Chart options for horizontal bar
  const horizontalBarOptions = useMemo(() => {
    const total = barChartData.total;
    return {
      chart: {
        type: 'bar',
        height: 140,
        toolbar: { show: false },
        sparkline: { enabled: false },
        parentHeightOffset: 0,
        animations: { enabled: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 3,
          barHeight: '65%',
          distributed: effectiveValueKeys.length === 1,
          dataLabels: { position: 'center' }
        }
      },
      colors: CHART_COLORS,
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: 600,
          colors: ['#fff']
        },
        formatter: (val) => {
          if (showPercentage && total > 0) {
            return ((val / total) * 100).toFixed(0) + '%';
          }
          return formatValue(val, true);
        },
        offsetX: 0,
        dropShadow: { enabled: false }
      },
      xaxis: {
        categories: barChartData.categories,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        labels: {
          show: true,
          style: { fontSize: '11px', colors: '#666' },
          minWidth: 80,
          maxWidth: 120,
          offsetX: 0,
          trim: false,
          hideOverlappingLabels: false,
          formatter: (val) => val
        }
      },
      grid: {
        show: false,
        padding: { left: 12, right: 5, top: -10, bottom: -5 }
      },
      tooltip: {
        enabled: true,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const fullName = barChartData.fullNames[dataPointIndex] || w.globals.labels[dataPointIndex];
          const val = series[seriesIndex][dataPointIndex];
          let displayVal = formatValue(val);
          if (showPercentage && total > 0) {
            const pct = ((val / total) * 100).toFixed(1);
            displayVal = `${formatValue(val)} (${pct}%)`;
          }
          return `<div style="padding: 8px 12px; font-size: 12px;"><strong>${fullName}</strong>: ${displayVal}</div>`;
        },
        style: { fontSize: '12px' }
      },
      legend: { show: false }
    };
  }, [barChartData.categories, barChartData.fullNames, barChartData.total, effectiveValueKeys.length, showPercentage, unit, isRatio, showRatioAsPercent]);

  // Chart options for vertical bar
  const verticalBarOptions = useMemo(() => {
    const total = barChartData.total;
    return {
      chart: {
        type: 'bar',
        height: 140,
        toolbar: { show: false },
        sparkline: { enabled: false },
        parentHeightOffset: 0,
        animations: { enabled: false },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 3,
          columnWidth: '60%',
          distributed: true,
          dataLabels: { position: 'top' }
        }
      },
      colors: CHART_COLORS,
      dataLabels: {
        enabled: true,
        style: { fontSize: '12px', fontWeight: 400, colors: ['#000'] },
        formatter: (val) => {
          if (showPercentage && total > 0) {
            return ((val / total) * 100).toFixed(0) + '%';
          }
          return formatValue(val, true);
        },
        offsetY: -18,
        background: { enabled: false },
        dropShadow: { enabled: false }
      },
      xaxis: {
        categories: barChartData.categories,
        labels: {
          show: true,
          style: { fontSize: '10px', colors: '#333', fontWeight: 500 },
          rotate: -20,
          rotateAlways: true,
          trim: false,
          hideOverlappingLabels: false,
          maxHeight: 42,
          offsetY: 0
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        labels: { show: false }
      },
      grid: {
        show: false,
        padding: { left: 8, right: 8, top: 0, bottom: 0 }
      },
      tooltip: {
        enabled: true,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const fullName = barChartData.fullNames[dataPointIndex] || w.globals.labels[dataPointIndex];
          const val = series[seriesIndex][dataPointIndex];
          let displayVal = formatValue(val);
          if (showPercentage && total > 0) {
            const pct = ((val / total) * 100).toFixed(1);
            displayVal = `${formatValue(val)} (${pct}%)`;
          }
          return `<div style="padding: 8px 12px; font-size: 12px;"><strong>${fullName}</strong>: ${displayVal}</div>`;
        },
        style: { fontSize: '12px' }
      },
      legend: { show: false }
    };
  }, [barChartData.categories, barChartData.fullNames, barChartData.total, showPercentage, unit, isRatio, showRatioAsPercent]);

  // Chart options for donut - Real-time percentage updates on hover/selection
  const donutOptions = useMemo(() => {
    const total = donutChartData.total;
    const seriesLength = donutChartData.series.length;
    // Calculate average percentage (100% / number of segments)
    const avgPercentage = seriesLength > 0 ? Math.round(100 / seriesLength) : 0;
    return {
      chart: {
        type: 'donut',
        height: 140,
        toolbar: { show: false },
        animations: { enabled: true, speed: 200 },
      },
      labels: donutChartData.labels,
      colors: CHART_COLORS,
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: 600,
          colors: ['#fff']
        },
        formatter: (val, opts) => {
          // Use the seriesIndex (or fallback to dataPointIndex) provided by Apex to map to raw series value
          try {
            const idx = opts ? (opts.seriesIndex ?? opts.dataPointIndex ?? 0) : 0;
            const raw = (donutChartData.series && donutChartData.series[idx] != null) ? donutChartData.series[idx] : val;
            return formatValue(raw);
          } catch (e) {
            return String(val);
          }
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '55%',
            labels: {
              show: true,
              name: {
                show: false,
              },
              value: {
                show: true,
                fontSize: '14px',
                fontWeight: 700,
                color: IQVIA_COLORS.darkBlue,
                offsetY: 5,
                formatter: (val) => {
                  // Show absolute value (with unit) instead of percent in the donut center
                  if (val === undefined || val === null || total === 0) return '0';
                  return formatValue(val);
                }
              },
              total: {
                show: true,
                showAlways: true,
                label: '',
                fontSize: '14px',
                fontWeight: 700,
                color: IQVIA_COLORS.darkBlue,
                formatter: () => {
                  // Show the sum of all values with a 'Total: ' prefix
                  const sum = donutChartData && typeof donutChartData.total === 'number' ? donutChartData.total : 0;
                  return `Total: ${formatValue(sum)}`;
                }
              }
            }
          }
        }
      },
      legend: {
        show: true,
        position: 'right',
        fontSize: '10px',
        fontFamily: 'inherit',
        offsetY: 0,
        offsetX: 4,
        height: 130,
        width: 130,
        itemMargin: {
          horizontal: 4,
          vertical: 1
        },
        markers: {
          width: 8,
          height: 8,
          radius: 8,
          offsetX: 0,
          offsetY: 0
        },
        formatter: (seriesName) => seriesName,
        onItemClick: {
          toggleDataSeries: false
        },
        onItemHover: {
          highlightDataSeries: true
        }
      },
      tooltip: {
        enabled: true,
        custom: ({ series, seriesIndex, w }) => {
          const fullName = donutChartData.fullNames[seriesIndex] || w.globals.labels[seriesIndex];
          const val = series[seriesIndex];
          const percentage = total > 0 ? ((val / total) * 100).toFixed(1) + '%' : '0%';
          return `<div style="padding: 8px 12px; font-size: 12px;"><strong>${fullName}</strong>: ${formatValue(val)} (${percentage})</div>`;
        },
        style: { fontSize: '12px' }
      },
      stroke: {
        width: 2,
        colors: ['#fff']
      },
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.9
          }
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'darken',
            value: 0.85
          }
        }
      }
    };
  }, [donutChartData.labels, donutChartData.fullNames, donutChartData.total]);

  // Chart options for line chart
  const lineChartOptions = useMemo(() => {
    return {
      chart: {
        type: 'line',
        height: 140,
        toolbar: { show: false },
        sparkline: { enabled: false },
        parentHeightOffset: 0,
        animations: { enabled: true, speed: 300 },
      },
      colors: CHART_COLORS,
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      markers: {
        size: 4,
        strokeWidth: 0,
        hover: { size: 6 }
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '10px',
          fontWeight: 500,
          colors: ['#333']
        },
        formatter: (val) => formatValue(val, true),
        offsetY: -8,
        background: { enabled: false }
      },
      xaxis: {
        categories: barChartData.categories,
        labels: {
          show: true,
          style: { fontSize: '10px', colors: '#666' },
          rotate: -45,
          rotateAlways: true,
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        labels: { show: false }
      },
      grid: {
        show: true,
        borderColor: '#f0f0f0',
        strokeDashArray: 3,
        padding: { left: 5, right: 5, top: 0, bottom: 0 }
      },
      tooltip: {
        enabled: true,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const fullName = barChartData.fullNames[dataPointIndex] || w.globals.labels[dataPointIndex];
          const val = series[seriesIndex][dataPointIndex];
          return `<div style="padding: 8px 12px; font-size: 12px;"><strong>${fullName}</strong>: ${formatValue(val)}</div>`;
        },
        style: { fontSize: '12px' }
      },
      legend: { show: false }
    };
  }, [barChartData.categories, barChartData.fullNames, isRatio, showRatioAsPercent]);

  // Get the icon component
  const IconComponent = ICON_MAP[iconType] || ICON_MAP.default;

  const hasData = effectiveData && effectiveData.length > 0;

  // Get tooltip text for abbreviated terms (e.g., "Res. Allocation" -> "Resource Allocation")
  const getTitleTooltip = (titleText) => {
    if (titleText && titleText.includes(TERMS.QUOTA)) {
      return titleText.replace(new RegExp(TERMS.QUOTA, 'g'), TERMS.QUOTA_FULL)
                      .replace(new RegExp(TERMS.QUOTA_PLURAL, 'g'), TERMS.QUOTA_FULL + 's');
    }
    return '';  // No tooltip needed if no abbreviated term
  };

  const titleTooltip = isDemoData
    ? DEMO_DATA_TOOLTIP
    : getTitleTooltip(title);

  // Render loading skeleton based on chart type
  const renderSkeleton = () => {
    if (chartType === 'donut') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: { xs: 0.5, md: 1 } }}>
          <Skeleton 
            variant="circular" 
            sx={{ 
              flexShrink: 0,
              width: { xs: 50, sm: 60, md: 70 },
              height: { xs: 50, sm: 60, md: 70 },
            }} 
            animation="wave" 
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.3, md: 0.5 }, flex: 1 }}>
            <Skeleton variant="rectangular" width="85%" sx={{ height: { xs: 8, md: 10 }, borderRadius: 1 }} animation="wave" />
            <Skeleton variant="rectangular" width="70%" sx={{ height: { xs: 8, md: 10 }, borderRadius: 1 }} animation="wave" />
            <Skeleton variant="rectangular" width="80%" sx={{ height: { xs: 8, md: 10 }, borderRadius: 1 }} animation="wave" />
            <Skeleton variant="rectangular" width="55%" sx={{ height: { xs: 8, md: 10 }, borderRadius: 1 }} animation="wave" />
            <Skeleton variant="rectangular" width="65%" sx={{ height: { xs: 8, md: 10 }, borderRadius: 1 }} animation="wave" />
          </Box>
        </Box>
      );
    } else if (chartType === 'verticalBar') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: '100%', pt: { xs: 1, md: 2 }, pb: { xs: 0.5, md: 1 } }}>
          <Skeleton variant="rectangular" sx={{ width: { xs: 12, sm: 14, md: 18 }, borderRadius: '3px 3px 0 0' }} height="55%" animation="wave" />
          <Skeleton variant="rectangular" sx={{ width: { xs: 12, sm: 14, md: 18 }, borderRadius: '3px 3px 0 0' }} height="75%" animation="wave" />
          <Skeleton variant="rectangular" sx={{ width: { xs: 12, sm: 14, md: 18 }, borderRadius: '3px 3px 0 0' }} height="40%" animation="wave" />
          <Skeleton variant="rectangular" sx={{ width: { xs: 12, sm: 14, md: 18 }, borderRadius: '3px 3px 0 0' }} height="65%" animation="wave" />
          <Skeleton variant="rectangular" sx={{ width: { xs: 12, sm: 14, md: 18 }, borderRadius: '3px 3px 0 0' }} height="50%" animation="wave" />
        </Box>
      );
    } else {
      // Horizontal bar skeleton
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, md: 0.75 }, height: '100%', justifyContent: 'center', py: { xs: 0.25, md: 0.5 } }}>
          {[70, 55, 85, 40, 65].map((width, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, md: 0.5 } }}>
              <Skeleton variant="text" sx={{ width: { xs: 35, sm: 40, md: 50 }, height: { xs: 10, md: 12 } }} animation="wave" />
              <Skeleton variant="rectangular" width={`${width}%`} sx={{ height: { xs: 10, md: 12 }, borderRadius: 1 }} animation="wave" />
            </Box>
          ))}
        </Box>
      );
    }
  };

  // Generate a stable key for the donut chart based on data to force re-render
  const donutChartKey = useMemo(() => {
    if (!donutChartData.series || donutChartData.series.length === 0) return 'empty';
    // Create a key based on the series values to force re-render when data changes
    return `donut-${donutChartData.series.join('-')}-${donutChartData.total}`;
  }, [donutChartData.series, donutChartData.total]);

  // Render chart based on type
  const renderChart = () => {
    if (chartType === 'waterfall') {
      // Waterfall: baseline (left), drivers (middle, may be + or -), final Forecast (right = baseline + sum(drivers))
      const items = effectiveData || [];
      if (!items || items.length === 0) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
            No data available
          </Box>
        );
      }

      const getVal = (it) => {
        const val = it?.[effectiveValueKeys[0]] ?? it?.[valueKey] ?? 0;
        const num = typeof val === 'number' ? val : parseFloat(val);
        return isNaN(num) ? 0 : num;
      };

      const baseline = getVal(items[0]);
      const drivers = items.slice(1).map(getVal);
      const driversNames = items.slice(1).map(it => it?.[nameKey] || '');
      const forecast = baseline + drivers.reduce((a, b) => a + b, 0);

      const values = [baseline, ...drivers, forecast];
      const categories = [truncateName(items[0]?.[nameKey] || 'Baseline', 16), ...driversNames.map(n => truncateName(n, 16)), 'Forecast'];

      const colors = [IQVIA_COLORS.lightBlue, ...drivers.map(d => (d < 0 ? '#d32f2f' : IQVIA_COLORS.lightBlue)), IQVIA_COLORS.darkBlue];

      const runningTotals = [];
      let cum = 0;
      values.forEach((v, i) => {
        if (i === 0) cum = v; else cum += (i === values.length - 1 ? 0 : v); // forecast is final total
        runningTotals.push(i === values.length - 1 ? forecast : cum);
      });

      const waterfallOptions = {
        chart: {
          type: 'bar',
          height: 140,
          toolbar: { show: false },
          sparkline: { enabled: false },
          parentHeightOffset: 0,
          animations: { enabled: false },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            barHeight: '60%',
            distributed: true,
          }
        },
        colors,
        dataLabels: {
          enabled: true,
          // Use per-point colors to match bar color and push labels outside the bar
          style: { fontSize: '12px', fontWeight: 600, colors: ['#000'] },
          formatter: (val, opts) => {
            const idx = opts.dataPointIndex;
            const rawVal = values[idx];
            const isEndpoint = (idx === 0 || idx === values.length - 1);

            if (!isEndpoint) {
              // Drivers: show signed full numeric value (e.g. +1250 or -150)
              const absFormatted = formatFullNumber(Math.abs(rawVal));
              const sign = rawVal > 0 ? '+' : '-';
              return `${sign}${absFormatted}`;
            }

            // Baseline / Forecast: show full numeric value (no K/M abbreviations)
            return formatFullNumber(rawVal);
          },
          offsetX: 8,
          background: { enabled: false },
          dropShadow: { enabled: false }
        },
        xaxis: {
          categories,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        tooltip: {
          enabled: true,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const fullName = (dataPointIndex === values.length - 1) ? 'Forecast' : (items[dataPointIndex]?.[nameKey] || w.globals.labels[dataPointIndex]);
            const val = series[seriesIndex][dataPointIndex];
            const rt = runningTotals[dataPointIndex];
            return `<div style="padding:8px 12px; font-size:12px;"><strong>${fullName}</strong>: ${formatValue(val)}<br/>Cumulative: ${formatValue(rt)}</div>`;
          },
          style: { fontSize: '12px' }
        },
        grid: { show: false, padding: { left: 0, right: 5, top: -10, bottom: -5 } },
        legend: { show: false }
      };

      return (
        <Chart
          options={waterfallOptions}
          series={[{ data: values }]}
          type="bar"
          height="100%"
        />
      );
    }
    if (chartType === 'donut') {
      return (
        <Box sx={{ width: '100%', height: '100%', overflow: 'visible', '& .apexcharts-legend': { overflow: 'visible !important' } }}>
          <Chart
            key={donutChartKey}
            options={donutOptions}
            series={donutChartData.series}
            type="donut"
            height="100%"
          />
        </Box>
      );
    } else if (chartType === 'verticalBar') {
      return (
        <Chart
          options={verticalBarOptions}
          series={barChartData.series}
          type="bar"
          height="100%"
        />
      );
    } else if (chartType === 'line') {
      return (
        <Chart
          options={lineChartOptions}
          series={barChartData.series}
          type="line"
          height="100%"
        />
      );
    } else {
      return (
        <Chart
          options={horizontalBarOptions}
          series={barChartData.series}
          type="bar"
          height="100%"
        />
      );
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        // Flex to fill available space in parent
        flex: '1 1 auto',
        // Responsive min/max widths - controls how many cards per row
        minWidth: {
          xs: 'calc(50% - 4px)',          // 2 cards per row on mobile
          sm: 'calc(50% - 6px)',          // 2 cards per row on small tablet
          md: 'calc(33% - 6px)',          // 3 cards per row on tablet+
          lg: 'calc(33% - 8px)',          // 3 cards per row on desktop
        },
        maxWidth: {
          xs: 'calc(50% - 4px)',
          sm: 'calc(50% - 6px)',
          md: 'calc(33% - 6px)',
          lg: 'calc(33% - 8px)',
        },
        // Responsive padding
        p: { xs: 0.75, sm: 0.75, md: 1 },
        background: '#ffffff',
        borderRadius: { xs: '6px', md: '8px' },
        border: '1px solid #e8eaed',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        // Allow optional row-stretch behavior for mixed-height rows
        height: stretchToRow ? 'auto' : '100%',
        alignSelf: stretchToRow ? 'stretch' : 'auto',
        // Min height to prevent cards from becoming too small
        minHeight: minHeightOverride || { xs: '90px', sm: '100px', md: '110px' },
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {/* Title with Icon */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: { xs: 0.3, md: 0.5 }, 
        mb: { xs: 0.3, md: 0.5 },
        flexShrink: 0,
      }}>
        <Box
          sx={{
            width: { xs: 16, sm: 18, md: 20 },
            height: { xs: 16, sm: 18, md: 20 },
            minWidth: { xs: 16, sm: 18, md: 20 },
            borderRadius: '4px',
            background: `linear-gradient(135deg, ${IQVIA_COLORS.lightBlue} 0%, ${IQVIA_COLORS.darkBlue} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconComponent sx={{ fontSize: { xs: 10, sm: 11, md: 12 }, color: '#fff' }} />
        </Box>
        <Tooltip title={titleTooltip} arrow placement="top" disableHoverListener={!titleTooltip}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '12px', sm: '13px', md: '15px' },
              textTransform: 'uppercase',
              letterSpacing: '0.2px',
              color: IQVIA_COLORS.charcoal,
              lineHeight: 1.2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              cursor: titleTooltip ? 'help' : 'default',
            }}
          >
            {title}
            {isDemoData && (
              <Box
                component="span"
                sx={{ color: '#80868b', fontWeight: 700, ml: 0.35 }}
              >
                *
              </Box>
            )}
          </Typography>
        </Tooltip>
      </Box>

      {/* Chart Area */}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', overflow: chartType === 'donut' ? 'visible' : 'hidden' }}>
        {chartType === 'waterfall' && unit && !isLoading && hasData && (
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 0, md: 2 },
              right: { xs: 0, md: 4 },
              zIndex: 2,
              fontSize: { xs: '9px', sm: '10px', md: '11px' },
              color: '#5f6368',
              fontWeight: 600,
              backgroundColor: 'rgba(255,255,255,0.85)',
              px: 0.75,
              py: 0.15,
              borderRadius: '6px',
              lineHeight: 1.2,
            }}
          >
            {unit}
          </Box>
        )}
        {isLoading ? (
          renderSkeleton()
        ) : hasData ? (
          renderChart()
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#999',
              fontSize: { xs: '9px', sm: '10px', md: '11px' },
            }}
          >
            No data available
          </Box>
        )}
      </Box>
    </Paper>
  );
});

MiniChartCard.displayName = 'MiniChartCard';

export default MiniChartCard;
